import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, SlidersHorizontal, X } from "lucide-react";
import { clsx } from "clsx";
import { Button, Dialog, Input, Select, Textarea } from "../ui";
import { atLeast } from "../app/settings";
import { TaskPicker } from "../tasks/TaskPicker";
import type { DayRef, FilterAst } from "../core/filter";
import {
  emptyGroup,
  newId,
  NO_VALUE,
  parse,
  toAst,
  type Group,
  type Item,
  type Mode,
  type Row,
} from "./model";
import {
  computedBoolKeys,
  defaultValue,
  fieldsOf,
  groupText,
  opsFor,
  signalKind,
  tagKeysOf,
  type FieldDef,
  type FilterSources,
} from "./fields";

/**
 * A *Filter* field (ADR 0020, FRONTEND_DESIGN §4.13): the filter in words and *Edit…*, which opens the builder.
 * Writes the same filter AST the JSON field used to take.
 */
export function FilterField({
  id,
  value,
  onChange,
  sources,
  testId = "filter-field",
}: {
  id?: string;
  value: FilterAst | undefined;
  onChange(next: FilterAst | undefined): void;
  sources: FilterSources;
  testId?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fields = useMemo(() => fieldsOf(sources, t), [sources, t]);
  const summary = useMemo(
    () => groupText(parse(value, computedBoolKeys()), fields, sources, t),
    [value, fields, sources, t],
  );
  const empty = !value || Object.keys(value).length === 0;
  return (
    <div className="flex min-w-0 items-start gap-2" data-testid={testId}>
      <p
        className={clsx("min-w-0 flex-1 pt-1 text-[12.5px] leading-snug", empty && "text-neutral-500")}
        data-testid="filter-summary"
      >
        {summary}
      </p>
      <Button
        id={id}
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="filter-edit"
      >
        <SlidersHorizontal size={13} aria-hidden /> {t("filters.edit")}
      </Button>
      {open && (
        <FilterDialog
          value={value}
          sources={sources}
          onApply={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export function FilterDialog({
  value,
  sources,
  onApply,
  onClose,
}: {
  value: FilterAst | undefined;
  sources: FilterSources;
  onApply(next: FilterAst | undefined): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const fields = useMemo(() => fieldsOf(sources, t), [sources, t]);
  const tagKeys = useMemo(() => tagKeysOf(sources.propDefs), [sources.propDefs]);
  const [root, setRoot] = useState<Group>(() => parse(value, computedBoolKeys()));
  const [asText, setAsText] = useState(false);
  const [text, setText] = useState("");
  const [textError, setTextError] = useState(false);
  const power = atLeast(sources.level, "power");

  const ast = asText ? undefined : toAst(root, tagKeys);
  const textAst = (): FilterAst | undefined | "invalid" => {
    if (!text.trim()) return undefined;
    try {
      return JSON.parse(text) as FilterAst;
    } catch {
      return "invalid";
    }
  };
  const result = asText ? textAst() : ast;
  const preview = result !== "invalid" && sources.preview ? sources.preview(result) : null;

  const toggleText = () => {
    if (!asText) {
      setText(ast && ast !== "invalid" ? JSON.stringify(ast, null, 2) : "");
      setAsText(true);
      return;
    }
    const a = textAst();
    if (a === "invalid") {
      setTextError(true);
      return;
    }
    setTextError(false);
    setRoot(parse(a, computedBoolKeys()));
    setAsText(false);
  };

  return (
    <Dialog
      title={t("filters.title")}
      onClose={onClose}
      testId="filter-dialog"
      footer={
        <>
          <p
            className="min-w-0 flex-1 text-[12.5px] text-neutral-500"
            data-testid="filter-preview"
            aria-live="polite"
          >
            {result === "invalid"
              ? t("filters.unfinished")
              : preview
                ? `${t("filters.matching", { n: preview.count })}${preview.titles.length ? `: ${preview.titles.join(", ")}${preview.count > preview.titles.length ? ", …" : ""}` : ""}`
                : ""}
          </p>
          {power && (
            <Button type="button" variant="ghost" size="sm" onClick={toggleText} data-testid="filter-as-text">
              {asText ? t("filters.asRows") : t("filters.asText")}
            </Button>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={result === "invalid"}
            onClick={() => result !== "invalid" && onApply(result)}
            data-testid="filter-apply"
          >
            {t("filters.apply")}
          </Button>
        </>
      }
    >
      {asText ? (
        <div className="flex flex-col gap-1.5">
          <Textarea
            rows={14}
            className="font-mono text-[12.5px]"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setTextError(false);
            }}
            aria-label={t("filters.asText")}
            aria-invalid={result === "invalid" || textError || undefined}
            data-testid="filter-text"
          />
          {(result === "invalid" || textError) && (
            <p className="text-[12px] text-err">{t("filters.badJson")}</p>
          )}
        </div>
      ) : (
        <GroupEditor group={root} onChange={setRoot} fields={fields} sources={sources} depth={0} />
      )}
    </Dialog>
  );
}

function GroupEditor({
  group,
  onChange,
  onRemove,
  fields,
  sources,
  depth,
}: {
  group: Group;
  onChange(g: Group): void;
  onRemove?(): void;
  fields: FieldDef[];
  sources: FilterSources;
  depth: number;
}) {
  const { t } = useTranslation();
  const set = (i: number, it: Item | null) => {
    const items = [...group.items];
    if (it) items[i] = it;
    else items.splice(i, 1);
    onChange({ ...group, items });
  };
  const add = (it: Item) => onChange({ ...group, items: [...group.items, it] });
  const firstField = fields.find((f) => f.id === "done")!;
  return (
    <div
      className={clsx(
        "flex flex-col gap-2",
        depth > 0 &&
          "rounded-md border border-neutral-200 bg-[var(--surface-2)] p-2.5 dark:border-neutral-800",
      )}
      data-testid="filter-group"
      data-mode={group.mode}
    >
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span>{depth === 0 ? t("filters.showMatching") : t("filters.subMatching")}</span>
        <Select
          compact
          value={group.mode}
          onChange={(e) => onChange({ ...group, mode: e.target.value as Mode })}
          aria-label={t("filters.modeLabel")}
          className="w-auto"
          data-testid="filter-mode"
        >
          <option value="all">{t("filters.mode.all")}</option>
          <option value="any">{t("filters.mode.any")}</option>
          <option value="none">{t("filters.mode.none")}</option>
        </Select>
        <span>{t("filters.ofThese")}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t("filters.removeGroup")}
            className="ml-auto grid h-7 w-7 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 touch:h-11 touch:w-11 dark:hover:bg-neutral-800"
          >
            <X size={14} aria-hidden />
          </button>
        )}
      </div>
      {group.items.length === 0 && depth === 0 && (
        <p className="text-[12.5px] text-neutral-500">{t("filters.noConditions")}</p>
      )}
      {group.items.map((it, i) =>
        it.kind === "group" ? (
          <GroupEditor
            key={it.id}
            group={it}
            onChange={(g) => set(i, g)}
            onRemove={() => set(i, null)}
            fields={fields}
            sources={sources}
            depth={depth + 1}
          />
        ) : it.kind === "raw" ? (
          <RawRow
            key={it.id}
            text={it.text}
            onChange={(text) => set(i, { ...it, text })}
            onRemove={() => set(i, null)}
          />
        ) : (
          <RowEditor
            key={it.id}
            row={it}
            onChange={(r) => set(i, r)}
            onRemove={() => set(i, null)}
            fields={fields}
            sources={sources}
          />
        ),
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => add({ kind: "row", id: newId(), field: firstField.id, op: "is", value: false })}
          data-testid="filter-add-row"
        >
          <Plus size={13} aria-hidden /> {t("filters.addCondition")}
        </Button>
        {depth < 3 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => add({ ...emptyGroup("any") })}
            data-testid="filter-add-group"
          >
            <Plus size={13} aria-hidden /> {t("filters.addGroup")}
          </Button>
        )}
      </div>
    </div>
  );
}

const SECTIONS = ["task", "where", "props", "computed", "signals"] as const;

function RowEditor({
  row,
  onChange,
  onRemove,
  fields,
  sources,
}: {
  row: Row;
  onChange(r: Row): void;
  onRemove(): void;
  fields: FieldDef[];
  sources: FilterSources;
}) {
  const { t } = useTranslation();
  const field = fields.find((f) => f.id === row.field);
  const ops = opsFor(field, sources);
  const known = !!field;
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="filter-row"
      data-field={row.field}
      data-op={row.op}
    >
      <Select
        compact
        className="w-auto max-w-[14rem]"
        value={row.field}
        aria-label={t("filters.fieldLabel")}
        onChange={(e) => {
          const f = fields.find((x) => x.id === e.target.value);
          const op = opsFor(f, sources)[0] ?? "eq";
          onChange({ ...row, field: e.target.value, op, value: defaultValue(f, op, sources) });
        }}
        data-testid="filter-row-field"
      >
        {!known && <option value={row.field}>{row.field}</option>}
        {SECTIONS.map((sec) => {
          const fs = fields.filter((f) => f.section === sec);
          return fs.length ? (
            <optgroup key={sec} label={t(`filters.section.${sec}`)}>
              {fs.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </optgroup>
          ) : null;
        })}
      </Select>
      {ops.length > 1 && (
        <Select
          compact
          className="w-auto"
          value={row.op}
          aria-label={t("filters.opLabel")}
          onChange={(e) => {
            const op = e.target.value;
            const keep =
              (["in", "notIn", "anyOf"].includes(op) === ["in", "notIn", "anyOf"].includes(row.op) &&
                !["next", "last", "between"].includes(op) &&
                !["next", "last", "between"].includes(row.op)) ||
              (["next", "last"].includes(op) && ["next", "last"].includes(row.op));
            onChange({
              ...row,
              op,
              value: keep && !NO_VALUE.has(row.op) ? row.value : defaultValue(field, op, sources),
            });
          }}
          data-testid="filter-row-op"
        >
          {ops.map((op) => (
            <option key={op} value={op}>
              {t(`filters.op.${op}`)}
            </option>
          ))}
        </Select>
      )}
      {field && !NO_VALUE.has(row.op) && (
        <ValueEditor
          row={row}
          field={field}
          sources={sources}
          onChange={(value) => onChange({ ...row, value })}
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("filters.removeCondition")}
        className="ml-auto grid h-7 w-7 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 touch:h-11 touch:w-11 dark:hover:bg-neutral-800"
        data-testid="filter-row-remove"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

function ValueEditor({
  row,
  field,
  sources,
  onChange,
}: {
  row: Row;
  field: FieldDef;
  sources: FilterSources;
  onChange(v: unknown): void;
}) {
  const { t } = useTranslation();
  const v = row.value;
  const label = t("filters.valueLabel");
  if (field.kind === "flag" || (field.kind === "signal" && signalKind(sources, field.id) === "boolean"))
    return (
      <Select
        compact
        className="w-auto"
        value={v ? "yes" : "no"}
        aria-label={label}
        onChange={(e) => onChange(e.target.value === "yes")}
        data-testid="filter-row-value"
      >
        <option value="no">{t("filters.no")}</option>
        <option value="yes">{t("filters.yes")}</option>
      </Select>
    );
  if (
    row.op === "next" ||
    row.op === "last" ||
    field.kind === "number" ||
    (field.kind === "signal" && signalKind(sources, field.id) === "number")
  )
    return (
      <span className="inline-flex items-center gap-1.5">
        <Input
          compact
          type="number"
          className="w-20"
          aria-label={label}
          value={typeof v === "number" ? v : ""}
          min={row.op === "next" || row.op === "last" ? 0 : undefined}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
          data-testid="filter-row-value"
        />
        {(row.op === "next" || row.op === "last") && (
          <span className="text-[12.5px]">{t("filters.days")}</span>
        )}
        {field.unit && <span className="text-[12.5px] text-neutral-500">{field.unit}</span>}
      </span>
    );
  if (field.kind === "date") {
    if (row.op === "between") {
      const pair = (Array.isArray(v) ? v : [{ days: 0 }, { days: 7 }]) as [DayRef, DayRef];
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <DayInput value={pair[0]} onChange={(d) => onChange([d, pair[1]])} />
          <span className="text-[12.5px]">{t("filters.and")}</span>
          <DayInput value={pair[1]} onChange={(d) => onChange([pair[0], d])} />
        </span>
      );
    }
    return <DayInput value={v as DayRef} onChange={onChange} />;
  }
  if (["in", "notIn", "anyOf"].includes(row.op)) {
    const picked = Array.isArray(v) ? (v as string[]) : [];
    return (
      <span
        className="inline-flex flex-wrap gap-1"
        role="group"
        aria-label={label}
        data-testid="filter-row-value"
      >
        {(field.options ?? []).map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={picked.includes(o.value)}
            onClick={() =>
              onChange(picked.includes(o.value) ? picked.filter((x) => x !== o.value) : [...picked, o.value])
            }
            className="h-[26px] rounded-sm border border-neutral-200 px-2 text-[12.5px] text-neutral-600 hover:bg-neutral-100 aria-pressed:border-accent aria-pressed:bg-accent-tint aria-pressed:text-accent touch:h-9 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            {o.label}
          </button>
        ))}
      </span>
    );
  }
  if (field.kind === "parent") {
    const chosen = sources.tasks.find((x) => x.id === v);
    return (
      <span className="inline-flex min-w-[12rem] flex-1 items-center gap-1.5" data-testid="filter-row-value">
        {chosen && <span className="truncate text-[12.5px]">{chosen.title}</span>}
        <TaskPicker
          tasks={sources.tasks}
          exclude={new Set()}
          onPick={(id) => onChange(id)}
          placeholder={chosen ? t("filters.otherTask") : t("filters.pickTask")}
          compact
        />
      </span>
    );
  }
  if (field.options && field.options.length)
    return (
      <Select
        compact
        className="w-auto max-w-[14rem]"
        value={typeof v === "string" ? v : ""}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        data-testid="filter-row-value"
      >
        {typeof v !== "string" && <option value="">—</option>}
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    );
  return (
    <Input
      compact
      className="w-44"
      aria-label={label}
      value={typeof v === "string" ? v : ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid="filter-row-value"
    />
  );
}

type DayKind = "today" | "tomorrow" | "yesterday" | "inDays" | "daysAgo" | "date";

/** A day: today, tomorrow, yesterday, in n days, n days ago (kept relative), or a date. */
function DayInput({ value, onChange }: { value: DayRef | undefined; onChange(d: DayRef): void }) {
  const { t } = useTranslation();
  const kind: DayKind =
    typeof value === "string"
      ? "date"
      : !value || value.days === 0
        ? "today"
        : value.days === 1
          ? "tomorrow"
          : value.days === -1
            ? "yesterday"
            : value.days > 0
              ? "inDays"
              : "daysAgo";
  const n = typeof value === "object" && value ? Math.abs(value.days) : 2;
  const pick = (k: DayKind) =>
    onChange(
      k === "today"
        ? { days: 0 }
        : k === "tomorrow"
          ? { days: 1 }
          : k === "yesterday"
            ? { days: -1 }
            : k === "inDays"
              ? { days: Math.max(2, n) }
              : k === "daysAgo"
                ? { days: -Math.max(2, n) }
                : new Date().toISOString().slice(0, 10),
    );
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="filter-day">
      <Select
        compact
        className="w-auto"
        value={kind}
        aria-label={t("filters.dayLabel")}
        onChange={(e) => pick(e.target.value as DayKind)}
      >
        {(["today", "tomorrow", "yesterday", "inDays", "daysAgo", "date"] as const).map((k) => (
          <option key={k} value={k}>
            {t(`filters.day.${k}`)}
          </option>
        ))}
      </Select>
      {(kind === "inDays" || kind === "daysAgo") && (
        <Input
          compact
          type="number"
          min={2}
          className="w-16"
          aria-label={t("filters.days")}
          value={n}
          onChange={(e) => {
            const x = Math.max(0, Math.round(Number(e.target.value)));
            onChange({ days: kind === "inDays" ? x : -x });
          }}
        />
      )}
      {kind === "date" && (
        <Input
          compact
          type="date"
          className="w-40"
          aria-label={t("filters.day.date")}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => e.target.value && onChange(e.target.value)}
        />
      )}
    </span>
  );
}

function RawRow({ text, onChange, onRemove }: { text: string; onChange(t: string): void; onRemove(): void }) {
  const { t } = useTranslation();
  let bad = false;
  try {
    JSON.parse(text);
  } catch {
    bad = true;
  }
  return (
    <div className="flex items-start gap-1.5" data-testid="filter-raw">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[12px] text-neutral-500">{t("filters.raw")}</span>
        <Textarea
          rows={2}
          className="font-mono text-[12px]"
          value={text}
          aria-label={t("filters.raw")}
          aria-invalid={bad || undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("filters.removeCondition")}
        className="mt-5 grid h-7 w-7 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 touch:h-11 touch:w-11 dark:hover:bg-neutral-800"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}
